/*
 * VersionCard.tsx : Un card qui permet de naviguer
 * entre les différentes versions d'un élément (ex : devis ou d'une commande),
 * et de créer ou supprimer des versions.
 */

// Import React
import React from 'react';

// Import Communs
import {
	DefinitionTermField,
	DefinitionDescriptionField,
	ConfirmDialog,
	SecondaryButton,
} from '../../../common';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
	faAngleLeft,
	faAngleRight,
	faTrash,
	faPlus,
} from '@fortawesome/free-solid-svg-icons';
// Props de VersionCard
interface VersionData {
	id?: number;
	versionIndex?: number | null;
	label?: string;
}

interface VersionCardProps {
	icon: IconProp;
	title: string;
	formData?: VersionData | null;
	versions: { id: number }[];
	loading: boolean;
	isStable: boolean;
	onNavigate: (id: number) => void;
	onAdd: () => void;
	onDelete: () => void;
	isDeleteDialogOpen: boolean;
	setIsDeleteDialogOpen: (open: boolean) => void;
	isAddDialogOpen: boolean;
	setIsAddDialogOpen: (open: boolean) => void;
}

// Composant VersionCard
const VersionCard: React.FC<VersionCardProps> = ({
	icon,
	title,
	formData,
	versions,
	loading,
	isStable,
	onNavigate,
	onAdd,
	onDelete,
	isDeleteDialogOpen,
	setIsDeleteDialogOpen,
	isAddDialogOpen,
	setIsAddDialogOpen,
}) => {
	const currentIndex = versions.findIndex((v) => v.id === formData?.id);
	const isValidIndex = currentIndex >= 0;
	const lastIndex = versions.length - 1;

	return (
		<div className="p-6 bg-white rounded-lg shadow-md">
			<h2 className="text-2xl font-bold mb-4">
				<FontAwesomeIcon icon={icon} className="w-6 h-6 text-gray-700" />{' '}
				{title} :
			</h2>

			<dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 items-center mb-2">
				{/* Indice de la version actuellement sélectionnée */}
				<DefinitionTermField
					label="Indice :"
					tooltip="Indice de la version actuellement sélectionnée"
				/>
				<div className="flex items-center gap-2">
					<DefinitionDescriptionField
						value={
							formData?.versionIndex != null
								? formData.versionIndex.toString()
								: '—'
						}
						loading={loading}
					/>

					{isValidIndex && currentIndex === lastIndex && currentIndex !== 0 && (
						<FontAwesomeIcon
							icon={faTrash}
							className="text-gray-300 hover:text-red-600 cursor-pointer"
							onClick={() => setIsDeleteDialogOpen(true)}
						/>
					)}

					{isStable && isValidIndex && currentIndex !== lastIndex && (
						<div className="px-3 ml-2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 rounded">
							Ancienne version.
						</div>
					)}
				</div>

				{/* Libellé de la version actuellement sélectionnée */}
				<DefinitionTermField
					label="Libellé :"
					tooltip="Libellé de la version actuellement sélectionnée"
				/>
				<DefinitionDescriptionField
					value={formData?.label || '—'}
					loading={loading}
				/>
			</dl>

			<div className="flex items-center gap-3 mt-3">
				<SecondaryButton
					onClick={() => {
						if (isValidIndex && currentIndex > 0) {
							onNavigate(versions[currentIndex - 1].id);
						}
					}}
					label="Précédente"
					size="sm"
					icon={faAngleLeft}
					fullWidth
					disabled={!isValidIndex || currentIndex <= 0}
					forceShowLabel={true}
				/>

				{isValidIndex && currentIndex === lastIndex ? (
					<>
						<SecondaryButton
							onClick={() => setIsAddDialogOpen(true)}
							label="Ajouter"
							size="sm"
							icon={faPlus}
							fullWidth
							forceShowLabel={true}
						/>
					</>
				) : (
					<SecondaryButton
						onClick={() => {
							if (isValidIndex && currentIndex < lastIndex) {
								onNavigate(versions[currentIndex + 1].id);
							}
						}}
						label="Suivante"
						size="sm"
						icon={faAngleRight}
						fullWidth
						invert
						disabled={!isValidIndex || currentIndex >= lastIndex}
						forceShowLabel={true}
					/>
				)}
			</div>

			{/* Dialogs */}
			<ConfirmDialog
				open={isDeleteDialogOpen}
				title="Supprimer cette version"
				description="Voulez-vous vraiment supprimer cette version ?"
				confirmColor="red"
				onConfirm={onDelete}
				onCancel={() => setIsDeleteDialogOpen(false)}
			/>

			<ConfirmDialog
				open={isAddDialogOpen}
				title="Créer une nouvelle version"
				description="Voulez-vous vraiment créer une nouvelle version ?"
				confirmColor="green"
				onConfirm={onAdd}
				onCancel={() => setIsAddDialogOpen(false)}
			/>
		</div>
	);
};

export default VersionCard;
